import { type MeetingDetail, MINUTES_CONTENT_FIELDS, type MinutesVersion } from '@fruuit/shared'
import { Badge, Button, Card, EmptyState, StatusBadge, useToast } from '@fruuit/ui'
import { FileText } from 'lucide-react'
import { useState } from 'react'
import { useApiMutation } from '../../api/hooks'
import { shortDate } from '../calendar/time'
import { type MinutesStep, meetingKeys, minutesCommands } from '../meetings/api'
import { ReasonDialog } from '../meetings/ReasonDialog'
import { MINUTES_LABELS, MinutesEditorDialog, type MinutesEditorMode } from './MinutesEditorDialog'

const STEP_COPY: Record<
  MinutesStep,
  { title: string; label: string; submit: string; optional?: boolean; done: string }
> = {
  submit: {
    title: 'Submit for review',
    label: 'Note for the reviewer',
    submit: 'Submit for review',
    optional: true,
    done: 'Minutes submitted for independent review.',
  },
  approve: {
    title: 'Approve minutes',
    label: 'Why do these minutes stand up?',
    submit: 'Approve',
    done: 'Minutes approved. Publish them when ready.',
  },
  publish: {
    title: 'Publish reviewed minutes',
    label: 'Publication note',
    submit: 'Publish to investors',
    done: 'Minutes published to subscribed investors.',
  },
  return: {
    title: 'Return for changes',
    label: 'What needs to change?',
    submit: 'Return to the author',
    done: 'Minutes returned to the author.',
  },
}

export function MinutesContent({ version }: { version: MinutesVersion }) {
  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-sage">
        <span className="font-semibold text-ink-soft">Participants:</span> {version.attendees}
      </p>
      {MINUTES_CONTENT_FIELDS.filter((f) => f !== 'attendees').map((field) => (
        <section key={field}>
          <h3 className="text-sm font-semibold text-teal-800">{MINUTES_LABELS[field]}</h3>
          <p className="mt-1 whitespace-pre-line text-[15px] leading-relaxed text-ink">
            {version[field]}
          </p>
        </section>
      ))}
    </div>
  )
}

type Pending =
  | { kind: 'editor'; mode: MinutesEditorMode; version?: MinutesVersion }
  | { kind: 'step'; step: MinutesStep; version: MinutesVersion }
  | null

/**
 * Reviewed minutes (S7, CAL-037, CAL-051): the current published version, the
 * published history, and for the case team the versions in progress with the
 * actions the server allows (P-UI-003: the server still decides).
 */
export function MinutesSection({ meeting, staff }: { meeting: MeetingDetail; staff: boolean }) {
  const { toast } = useToast()
  const [pending, setPending] = useState<Pending>(null)
  const published = meeting.minutes.filter((v) => v.state === 'published')
  const current = published.find((v) => v.is_current) ?? published[0]
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const shown = published.find((v) => v.id === selectedId) ?? current
  const inProgress = meeting.minutes.filter(
    (v) => v.state !== 'published' && v.state !== 'retracted',
  )
  const invalidate = [meetingKeys.all]

  const create = useApiMutation(
    (body: Record<string, unknown>, ctx) =>
      minutesCommands.create({ meeting_id: meeting.id, ...body }, ctx),
    { invalidate },
  )
  const update = useApiMutation(
    ({ id, body }: { id: string; body: Record<string, unknown> }, ctx) =>
      minutesCommands.update(id, body, ctx),
    { invalidate },
  )
  const correct = useApiMutation(
    ({ id, body }: { id: string; body: Record<string, unknown> }, ctx) =>
      minutesCommands.correct(id, body, ctx),
    { invalidate },
  )
  const step = useApiMutation(
    (
      { id, step: s, body }: { id: string; step: MinutesStep; body: Record<string, unknown> },
      ctx,
    ) => minutesCommands.step(id, s, body, ctx),
    { invalidate },
  )

  async function saveEditor(body: Record<string, unknown>) {
    if (pending?.kind !== 'editor') return
    if (pending.mode === 'create') await create.mutateAsync(body)
    else if (pending.mode === 'edit' && pending.version)
      await update.mutateAsync({ id: pending.version.id, body })
    else if (pending.mode === 'correct' && pending.version)
      await correct.mutateAsync({ id: pending.version.id, body })
    toast({ tone: 'success', message: 'Draft minutes saved. Nothing is visible to investors yet.' })
  }

  async function runStep(reason: string) {
    if (pending?.kind !== 'step') return
    const { version, step: s } = pending
    const body =
      s === 'submit'
        ? { revision: version.revision, ...(reason ? { note: reason } : {}) }
        : { revision: version.revision, reason }
    await step.mutateAsync({ id: version.id, step: s, body })
    toast({ tone: 'success', message: STEP_COPY[s].done })
  }

  const stepCopy = pending?.kind === 'step' ? STEP_COPY[pending.step] : null

  return (
    <Card
      title="Reviewed meeting minutes"
      actions={
        shown ? (
          <span className="text-sm text-sage">
            Version {shown.version} · released {shortDate(shown.published_at)}
          </span>
        ) : null
      }
    >
      {shown ? (
        <>
          {shown.is_current ? null : (
            <p className="mb-4 rounded-md bg-watch-soft px-3 py-2 text-sm text-watch">
              You are reading version {shown.version}. Version {shown.superseded_by_version}{' '}
              corrects it.
            </p>
          )}
          <MinutesContent version={shown} />
          <p className="mt-5 text-xs text-sage">
            Released {shortDate(shown.published_at)} after independent review
            {shown.approved_by ? ` by ${shown.approved_by.name}` : ''}.
          </p>
        </>
      ) : (
        <EmptyState
          variant="plain"
          icon={<FileText />}
          title="No reviewed minutes have been released yet."
          description="Minutes appear here once the case reviewer has approved and published them."
        />
      )}

      {published.length > 1 ? (
        <div className="mt-6 border-t border-line pt-4">
          <h3 className="text-sm font-semibold text-ink">Published history</h3>
          <div className="mt-2 flex flex-wrap gap-2">
            {published.map((v) => (
              <Button
                key={v.id}
                size="sm"
                variant={v.id === shown?.id ? 'primary' : 'secondary'}
                aria-pressed={v.id === shown?.id}
                onClick={() => setSelectedId(v.id)}
              >
                Version {v.version} · {shortDate(v.published_at)}
                {v.is_current ? ' (current)' : ''}
              </Button>
            ))}
          </div>
        </div>
      ) : null}

      {staff ? (
        <div className="mt-6 flex flex-col gap-4 border-t border-line pt-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-ink">Team work in progress</h3>
            <div className="flex flex-wrap gap-2">
              {meeting.permissions?.add_minutes && !current ? (
                <Button size="sm" onClick={() => setPending({ kind: 'editor', mode: 'create' })}>
                  Add meeting minutes
                </Button>
              ) : null}
              {current?.permissions?.correct ? (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setPending({ kind: 'editor', mode: 'correct', version: current })}
                >
                  Correct published minutes
                </Button>
              ) : null}
            </div>
          </div>
          {inProgress.length === 0 ? (
            <p className="text-sm text-sage">No unpublished minutes.</p>
          ) : (
            inProgress.map((v) => (
              <div key={v.id} className="rounded-lg border border-line bg-cream/60 p-4">
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <strong className="text-sm">Minutes v{v.version}</strong>
                  <StatusBadge status={v.state} />
                  {v.previous_id ? <Badge tone="outline">Correction</Badge> : null}
                  <span className="text-xs text-sage">
                    {v.author ? `by ${v.author.name}` : ''} · updated {shortDate(v.updated_at)}
                  </span>
                </div>
                {v.change_reason ? (
                  <p className="mb-2 text-sm text-ink-soft">
                    <span className="font-semibold">Correction:</span> {v.change_reason}
                  </p>
                ) : null}
                {v.return_reason && v.state === 'returned' ? (
                  <p className="mb-2 text-sm text-attention">
                    <span className="font-semibold">Returned:</span> {v.return_reason}
                  </p>
                ) : null}
                {v.approval_reason ? (
                  <p className="mb-2 text-sm text-ink-soft">
                    <span className="font-semibold">Approval:</span> {v.approval_reason}
                  </p>
                ) : null}
                <details className="mb-3">
                  <summary className="cursor-pointer text-sm font-semibold text-teal">
                    Read the draft
                  </summary>
                  <div className="mt-3">
                    <MinutesContent version={v} />
                  </div>
                </details>
                <div className="flex flex-wrap gap-2">
                  {v.permissions?.edit ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setPending({ kind: 'editor', mode: 'edit', version: v })}
                    >
                      Edit draft
                    </Button>
                  ) : null}
                  {v.permissions?.submit ? (
                    <Button
                      size="sm"
                      onClick={() => setPending({ kind: 'step', step: 'submit', version: v })}
                    >
                      Submit for review
                    </Button>
                  ) : null}
                  {v.permissions?.approve ? (
                    <Button
                      size="sm"
                      onClick={() => setPending({ kind: 'step', step: 'approve', version: v })}
                    >
                      Approve minutes
                    </Button>
                  ) : null}
                  {v.permissions?.publish ? (
                    <Button
                      size="sm"
                      onClick={() => setPending({ kind: 'step', step: 'publish', version: v })}
                    >
                      Publish reviewed minutes
                    </Button>
                  ) : null}
                  {v.permissions?.return ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setPending({ kind: 'step', step: 'return', version: v })}
                    >
                      Return for changes
                    </Button>
                  ) : null}
                </div>
              </div>
            ))
          )}
        </div>
      ) : null}

      <MinutesEditorDialog
        open={pending?.kind === 'editor'}
        onClose={() => setPending(null)}
        mode={pending?.kind === 'editor' ? pending.mode : 'create'}
        {...(pending?.kind === 'editor' && pending.version ? { version: pending.version } : {})}
        onSubmit={saveEditor}
      />
      <ReasonDialog
        open={pending?.kind === 'step'}
        onClose={() => setPending(null)}
        title={stepCopy?.title ?? ''}
        label={stepCopy?.label ?? ''}
        submitLabel={stepCopy?.submit ?? 'Confirm'}
        optional={stepCopy?.optional ?? false}
        onSubmit={runStep}
      />
    </Card>
  )
}
